/* @moldura padrao */
/**
 * form.tsx (viagens) — Criar/editar viagem (roteiro). Tema AdminUX (Bootstrap 5).
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { sugerirNomeCurto } from '@/lib/viagem';
import type {
  Instalador,
  LojaOpcao,
  StatusViagem,
  TipoViagem,
  Transporte,
  ViagemDetalhe,
} from './shared';
import { agregaOS, STATUS_VIAGEM, TIPOS_VIAGEM, TRANSPORTES } from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { useAuth } from '@/auth/auth-context';

interface FormState {
  origem: string;
  nome_curto: string;
  tipo: TipoViagem;
  loja_id: string;
  data_partida: string;
  data_chegada: string;
  transporte: Transporte;
  status: StatusViagem;
  observacoes: string;
}

const VAZIO: FormState = {
  origem: '',
  nome_curto: '',
  tipo: 'operacional',
  loja_id: '',
  data_partida: '',
  data_chegada: '',
  transporte: 'carro',
  status: 'planejada',
  observacoes: '',
};

export function ViagemFormPage() {
  const { id } = useParams();
  const edicao = Boolean(id);
  const navigate = useNavigate();
  const { pode } = useAuth();

  const [form, setForm] = useState<FormState>(VAZIO);
  const [destinos, setDestinos] = useState<string[]>(['']);
  const [instaladoresSel, setInstaladoresSel] = useState<number[]>([]);

  /* O nome curto que o app sugere para o roteiro atual — o mesmo cálculo que a
     agenda usa quando a viagem não tem nome gravado. Vive como placeholder do
     campo: enquanto ela não digitar nada, é isto que vai aparecer na pílula. */
  const nomeSugerido = useMemo(() => sugerirNomeCurto(destinos), [destinos]);

  const [opcoesInstaladores, setOpcoesInstaladores] = useState<Instalador[]>([]);
  const [opcoesParticipantes, setOpcoesParticipantes] = useState<Instalador[]>([]);
  const [lojas, setLojas] = useState<LojaOpcao[]>([]);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [confirmarRemover, setConfirmarRemover] = useState(false);
  const [removendo, setRemovendo] = useState(false);

  // Viagem operacional é feita por instaladores; a de sócios/administrativa, por
  // qualquer usuário (as sócias não têm perfil 'instalador').
  const comOS = agregaOS(form.tipo);
  const opcoesEquipe = comOS ? opcoesInstaladores : opcoesParticipantes;

  function set<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function trocarTipo(novo: TipoViagem) {
    set('tipo', novo);
  }

  useEffect(() => {
    let vivo = true;
    async function init() {
      setCarregando(true);
      try {
        const [insts, parts, lojasResp] = await Promise.all([
          api.get<Instalador[]>('/viagens/instaladores').catch(() => []),
          api.get<Instalador[]>('/viagens/participantes').catch(() => []),
          api.get<LojaOpcao[]>('/lojas').catch(() => []),
        ]);
        if (!vivo) return;
        setOpcoesInstaladores(insts);
        setOpcoesParticipantes(parts);
        setLojas(lojasResp);

        if (edicao) {
          const v = await api.get<ViagemDetalhe>(`/viagens/${id}`);
          if (!vivo) return;
          setForm({
            origem: v.origem ?? '',
            nome_curto: v.nome_curto ?? '',
            tipo: v.tipo ?? 'operacional',
            loja_id: v.loja_id ? String(v.loja_id) : '',
            data_partida: v.data_partida ?? '',
            data_chegada: v.data_chegada ?? '',
            transporte: v.transporte,
            status: v.status,
            observacoes: v.observacoes ?? '',
          });
          setDestinos(v.destinos?.length ? v.destinos : ['']);
          setInstaladoresSel((v.instaladores ?? []).map((x) => x.id));
        } else {
          // Viagem nova: Búzios por padrão (ajustável).
          const bz = lojasResp.find((l) => l.codigo === 'BZ');
          if (bz) setForm((f) => ({ ...f, loja_id: String(bz.id) }));
        }
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : 'Erro ao carregar a viagem.',
        );
      } finally {
        if (vivo) setCarregando(false);
      }
    }
    init();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Destinos (lista dinâmica e ordenada) ──────────────────────────────────
  function setDestino(i: number, valor: string) {
    setDestinos((arr) => arr.map((d, idx) => (idx === i ? valor : d)));
  }
  function addDestino() {
    setDestinos((arr) => [...arr, '']);
  }
  function rmDestino(i: number) {
    setDestinos((arr) => arr.filter((_, idx) => idx !== i));
  }

  function toggleInstalador(uid: number) {
    setInstaladoresSel((arr) =>
      arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid],
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.origem.trim()) {
      toast.error('Informe a origem da viagem.');
      return;
    }
    setSalvando(true);
    const payload = {
      origem: form.origem.trim(),
      /* Igual à sugestão = não é escolha dela, é o padrão; grava NULL para o
         nome continuar acompanhando o roteiro se uma parada mudar depois. */
      nome_curto:
        form.nome_curto.trim() && form.nome_curto.trim() !== nomeSugerido
          ? form.nome_curto.trim()
          : null,
      tipo: form.tipo,
      loja_id: form.loja_id ? Number(form.loja_id) : null,
      data_partida: form.data_partida || null,
      data_chegada: form.data_chegada || null,
      transporte: form.transporte,
      status: form.status,
      observacoes: form.observacoes.trim() || null,
      destinos: destinos.map((d) => d.trim()).filter(Boolean),
      instaladores: instaladoresSel,
      os_ids: [], // Fatia 4: OS vêm dos eventos (calendário); backend ignora este campo.
    };
    try {
      if (edicao) {
        await api.put<ViagemDetalhe>(`/viagens/${id}`, payload);
        toast.success('Viagem salva.');
        navigate(`/viagens/${id}`);
        return;
      }
      const criada = await api.post<ViagemDetalhe>('/viagens', payload);
      toast.success('Viagem criada.');
      navigate(`/viagens/${criada.id}`);
      return;
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível salvar a viagem.',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!id) return;
    setRemovendo(true);
    try {
      await api.delete(`/viagens/${id}`);
      toast.success('Viagem removida.');
      navigate('/viagens');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível remover.',
      );
    } finally {
      setRemovendo(false);
      setConfirmarRemover(false);
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
        <span className="spinner-border spinner-border-sm" />
        Carregando…
      </div>
    );
  }

  return (
    <CustomMolduraPagina
      titulo={edicao ? 'Editar viagem' : 'Nova viagem'}
      descricao="Roteiro, equipe e OS da viagem"
      caminho={[{ rotulo: 'Viagens', para: '/viagens' }]}
    >

      <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        {/* Tipo da viagem — define se ela agrega OS e quem pode participar */}
        <div className="card adminuiux-card">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-signpost text-theme-1 me-2"></i>
              Tipo de viagem
            </h6>
            <div className="row g-2">
              {TIPOS_VIAGEM.map((t) => {
                const on = form.tipo === t.value;
                return (
                  <div key={t.value} className="col-12 col-md-4">
                    <button
                      type="button"
                      className={`btn w-100 text-start p-3 ${on ? 'btn-theme' : 'btn-outline-theme'}`}
                      onClick={() => trocarTipo(t.value)}
                    >
                      <span className="d-block fw-semibold mb-1">
                        <i className={`bi ${t.icon} me-2`}></i>
                        {t.label}
                        {on && <i className="bi bi-check2 ms-2"></i>}
                      </span>
                      <span className="d-block small opacity-75" style={{ whiteSpace: 'normal' }}>
                        {t.desc}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Roteiro */}
        <div className="card adminuiux-card">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-signpost-split text-theme-1 me-2"></i>
              Roteiro
            </h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="campo-origem"
                    className="form-control"
                    placeholder="Origem"
                    value={form.origem}
                    onChange={(e) => set('origem', e.target.value)}
                    required
                  />
                  <label htmlFor="campo-origem">Origem</label>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <select
                    id="campo-loja"
                    className="form-select"
                    value={form.loja_id}
                    onChange={(e) => set('loja_id', e.target.value)}
                  >
                    <option value="">—</option>
                    {lojas.map((l) => (
                      <option key={l.id} value={String(l.id)}>
                        {l.nome}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="campo-loja">Loja (empresa)</label>
                </div>
              </div>

              <div className="col-12">
                <label className="form-label mb-2">Destinos (em ordem)</label>
                <div className="d-flex flex-column gap-2">
                  {destinos.map((d, i) => (
                    <div key={i} className="d-flex gap-2 align-items-center">
                      <span className="text-secondary small" style={{ width: '1.2rem' }}>
                        {i + 1}.
                      </span>
                      <input
                        className="form-control"
                        placeholder={`Destino ${i + 1}`}
                        value={d}
                        onChange={(e) => setDestino(i, e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-link btn-square text-danger"
                        onClick={() => rmDestino(i)}
                        aria-label="Remover destino"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-theme"
                      onClick={addDestino}
                    >
                      <i className="bi bi-plus-lg me-1"></i> destino
                    </button>
                  </div>
                </div>
              </div>

              {/* NOME CURTO — logo abaixo do roteiro, porque é dele que sai.
                  Em branco vale a sugestão, e é isso que a agenda mostra; o
                  campo existe para quando a sugestão não servir. Deixar em
                  branco é melhor que aceitar a sugestão digitada: assim, mudar
                  uma parada depois corrige o nome sozinho. */}
              <div className="col-12">
                <div className="form-floating">
                  <input
                    id="campo-nome-curto"
                    className="form-control"
                    maxLength={40}
                    placeholder="Nome curto"
                    value={form.nome_curto}
                    onChange={(e) => set('nome_curto', e.target.value)}
                  />
                  <label htmlFor="campo-nome-curto">Nome curto (na agenda)</label>
                </div>
                <div className="form-text d-flex flex-wrap align-items-center gap-2">
                  <span>
                    É assim que a viagem aparece na pílula do dia. Em branco,
                    vale a sugestão:{' '}
                    <strong>{nomeSugerido || '—'}</strong>
                  </span>
                  {form.nome_curto.trim() !== '' && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => set('nome_curto', '')}
                    >
                      voltar à sugestão
                    </button>
                  )}
                </div>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label">Data de partida</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.data_partida}
                  onChange={(e) => set('data_partida', e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label">Data de chegada</label>
                <input
                  type="date"
                  className="form-control"
                  min={form.data_partida || undefined}
                  value={form.data_chegada}
                  onChange={(e) => set('data_chegada', e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <div className="form-floating">
                  <select
                    id="campo-transporte"
                    className="form-select"
                    value={form.transporte}
                    onChange={(e) =>
                      set('transporte', e.target.value as Transporte)
                    }
                  >
                    {TRANSPORTES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="campo-transporte">Transporte</label>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="form-floating">
                  <select
                    id="campo-status"
                    className="form-select"
                    value={form.status}
                    disabled={form.status === 'cancelada'}
                    onChange={(e) =>
                      set('status', e.target.value as StatusViagem)
                    }
                  >
                    {/* "Cancelada" não se escolhe aqui: cancelar pede o
                        motivo, e o motivo é o botão Cancelar viagem do
                        detalhe (03/11). Na cancelada, o select trava nela. */}
                    {STATUS_VIAGEM.filter(
                      (s) => s.value !== 'cancelada' || form.status === 'cancelada',
                    ).map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="campo-status">Status</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Equipe — instaladores na operacional; qualquer usuário nas demais */}
        <div className="card adminuiux-card">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-people text-theme-1 me-2"></i>
              {comOS ? 'Instaladores' : 'Quem viaja'}
            </h6>
            {opcoesEquipe.length === 0 ? (
              <span className="small text-secondary">
                {comOS ? 'Nenhum instalador cadastrado.' : 'Nenhum usuário ativo.'}
              </span>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {opcoesEquipe.map((inst) => {
                  const on = instaladoresSel.includes(inst.id);
                  return (
                    <button
                      key={inst.id}
                      type="button"
                      className={`btn btn-sm ${on ? 'btn-theme' : 'btn-outline-theme'}`}
                      onClick={() => toggleInstalador(inst.id)}
                    >
                      {on && <i className="bi bi-check me-1"></i>}
                      {inst.nome}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* OS da viagem — agora vêm da Agenda (Fatia 4), não se escolhem aqui. */}
        {comOS && (
          <div className="card adminuiux-card">
            <div className="card-body">
              <h6 className="mb-2">
                <i className="bi bi-clipboard-check text-theme-1 me-2"></i>
                OS da viagem
              </h6>
              <p className="small text-secondary mb-0">
                As OS entram pela <strong>Agenda</strong>: no calendário, ligue cada
                evento à viagem pelo chip de viagem no card. As OS desta viagem
                aparecem na tela de detalhe.
              </p>
            </div>
          </div>
        )}

        {/* Observações */}
        <div className="card adminuiux-card">
          <div className="card-body">
            <h6 className="mb-3">
              <i className="bi bi-card-text text-theme-1 me-2"></i>
              Observações
            </h6>
            <textarea
              className="form-control"
              style={{ height: '100px' }}
              placeholder="Notas da viagem (logística, hospedagem, etc.)"
              value={form.observacoes}
              onChange={(e) => set('observacoes', e.target.value)}
            />
          </div>
        </div>

        {/* Ações */}
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div>
            {/* Mesma trava da rota (DELETE /viagens/{id}). Sem ela a Patrícia via o
                botão e levava 403 — o gestor-financeiro edita viagem, não apaga. */}
            {edicao && pode('viagens.viagem.excluir') && (
              <button
                type="button"
                className="btn btn-outline-theme text-danger"
                onClick={() => setConfirmarRemover(true)}
              >
                <i className="bi bi-trash me-1"></i> Remover
              </button>
            )}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-link"
              onClick={() => navigate(edicao ? `/viagens/${id}` : '/viagens')}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-theme" disabled={salvando}>
              {salvando && (
                <span className="spinner-border spinner-border-sm me-1" />
              )}
              Salvar
            </button>
          </div>
        </div>
      </form>

      {/* Modal: confirmar remoção */}
      <Modal
        className="casaa-modal"
        show={confirmarRemover}
        onHide={() => setConfirmarRemover(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remover viagem</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem certeza que deseja remover esta viagem? Esta ação não pode ser
          desfeita.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setConfirmarRemover(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={remover}
            disabled={removendo}
          >
            {removendo && (
              <span className="spinner-border spinner-border-sm me-1" />
            )}
            Remover
          </button>
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
